import { FeatureKeys } from '@/config';
import { CanAccess } from '@/providers/CanAccess';
import { useUpgradeModal } from '@/hooks/useUpgradeModal';
import {
  Bars3BottomRightIcon,
  MagnifyingGlassIcon,
  Squares2X2Icon,
} from '@heroicons/react/16/solid';
import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { tv } from 'tailwind-variants';

const getTabItems = (urlHash: string) => {
  const baseUrl = '/analyze';
  const conditionUrlHashValue = new URLSearchParams(urlHash.slice(1)).get('q');
  const conditionUrlHash = conditionUrlHashValue
    ? `#q=${encodeURIComponent(conditionUrlHashValue)}`
    : '';

  return [
    {
      featureKey: null,
      label: 'Search',
      href: `${baseUrl}/search${conditionUrlHash}`,
      icon: <MagnifyingGlassIcon />,
    },
    {
      featureKey: FeatureKeys.treemap,
      label: 'TreeMap',
      href: `${baseUrl}/treemap${conditionUrlHash}`,
      icon: <Squares2X2Icon />,
    },
    {
      featureKey: FeatureKeys.transition,
      label: 'Transition',
      href: `${baseUrl}/transition${conditionUrlHash}`,
      icon: <Bars3BottomRightIcon />,
    },
  ];
};

const tabStyle = tv({
  base: 'relative flex items-center justify-center rounded-tr-lg border-2 border-b-0 px-3 py-1.5',
  variants: {
    isActive: {
      true: 'pointer-events-none border-x-primary-teal border-t-primary-teal bg-white text-primary-teal',
      false: 'border-transparent hover:text-gray-600',
    },
  },
});

const TabItem = ({
  href,
  icon,
  label,
}: {
  href: string;
  icon: React.ReactElement;
  label: string;
}) => {
  return (
    <NavLink to={href}>
      {({ isActive }) => (
        <>
          <div className={tabStyle({ isActive })}>
            {icon && React.cloneElement(icon, { className: 'me-1.5 size-4' })}
            <span className="mr-1" translate="no">
              {label}
            </span>
          </div>
          {/* アクティブなタブの下に白い要素を絶対位置で配置 */}
          {isActive && (
            <div className="absolute inset-x-0.5 bottom-[-3px] z-50 h-2 bg-white"></div>
          )}
        </>
      )}
    </NavLink>
  );
};

const LockedTabItem = ({
  icon,
  label,
  onClick,
}: {
  icon: React.ReactElement;
  label: string;
  onClick: () => void;
}) => {
  return (
    <button type="button" onClick={onClick} className="relative">
      <div className={tabStyle({ isActive: false }) + ' opacity-50'}>
        {icon && React.cloneElement(icon, { className: 'me-1.5 size-4' })}
        <span className="mr-1" translate="no">
          {label}
        </span>
      </div>
    </button>
  );
};

export const AnalyzeTab = React.memo(() => {
  const { hash } = useLocation();
  const { open } = useUpgradeModal();
  const tabItems = getTabItems(hash);

  return (
    <div className="relative mt-5 flex flex-wrap text-center text-sm font-medium text-gray-500">
      {tabItems.map(({ featureKey, label, href, icon }, index) => {
        return featureKey === null ? (
          <div key={index} className="relative">
            <TabItem href={href} icon={icon} label={label} />
          </div>
        ) : (
          <CanAccess
            key={index}
            featureKey={featureKey}
            FallbackComponent={
              <div className="relative">
                <LockedTabItem icon={icon} label={label} onClick={open} />
              </div>
            }
          >
            <div className="relative">
              <TabItem href={href} icon={icon} label={label} />
            </div>
          </CanAccess>
        );
      })}
    </div>
  );
});

AnalyzeTab.displayName = 'AnalyzeTab';
